import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Cell, ReferenceLine } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import type { MonthlyData } from "@/hooks/admin/AdminDashboardTab/usePropertyYearlyMetrics";

interface PropertyAccumulatedChartProps {
  data?: MonthlyData[];
  isLoading?: boolean;
  year: number;
}

const chartConfig = {
  accumulated: {
    label: "Acumulado",
    color: "hsl(var(--success))",
  },
} satisfies ChartConfig;

const PropertyAccumulatedChart = ({
  data = [],
  isLoading = false,
  year,
}: PropertyAccumulatedChartProps) => {
  // Prepare chart data with positive/negative indication
  const chartData = data.map((item) => ({
    month: item.month.substring(0, 3), // Shorten month name
    fullMonth: item.month,
    accumulated: item.amount,
    isPositive: item.amount >= 0,
  }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Utilidad Acumulada {year}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Calculate min/max for Y axis domain
  const allValues = chartData.map(d => d.accumulated);
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues, 0);
  const padding = Math.abs(maxValue - minValue) * 0.1 || 1000;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Utilidad Acumulada {year}</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <BarChart 
            data={chartData} 
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
            <XAxis 
              dataKey="month" 
              tickLine={false} 
              axisLine={false}
              tick={{ fontSize: 12 }}
              className="fill-muted-foreground"
            />
            <YAxis 
              tickLine={false} 
              axisLine={false}
              tickFormatter={(value) => {
                const absValue = Math.abs(value);
                if (absValue >= 1000000) {
                  return `$${(value / 1000000).toFixed(1)}M`;
                }
                return `$${(value / 1000).toFixed(0)}k`;
              }}
              tick={{ fontSize: 12 }}
              width={60}
              domain={[minValue - padding, maxValue + padding]}
              className="fill-muted-foreground"
            />
            <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.5} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value) => {
                    const formattedValue = new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                      minimumFractionDigits: 0,
                    }).format(Number(value));
                    return <span>Acumulado: {formattedValue}</span>;
                  }}
                  labelFormatter={(_, payload) => {
                    if (payload && payload[0]) {
                      return payload[0].payload.fullMonth;
                    }
                    return "";
                  }}
                />
              }
            />
            <Bar 
              dataKey="accumulated" 
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`}
                  fill={entry.isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"}
                />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
        
        {/* Legend */}
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-success" />
            <span className="text-muted-foreground">Ganancia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-destructive" />
            <span className="text-muted-foreground">Pérdida</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyAccumulatedChart;
